import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Button, Popup } from 'semantic-ui-react';

import { localeData } from '../../../locales/data';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { isMobile } from '../../../shared/helpers/responsive';

import EditorSettingsForm from './EditorSettingsForm';
import './EditorTopBarLeft.scss';
import analytics from '../../../shared/services/analytics/analytics';

interface EditorTopBarLeftProps {
  currentRegion?: string;
  currentDelay?: string;
  currentUniverse?: string;
  isMultiSimulation: boolean;
  isChild: boolean;
  handlerChangeToMultiSimulation?: () => void;
  modifiers: string[];
  settingsOptions: any;
  onClickCode: () => void;
  onClickResults: () => void;
  code: boolean;
  results: boolean;
  isSuperAlpha: boolean;
}

interface EditorTopBarLeftState {
  isOpen: boolean;
  heightSettings: number;
}

type EditorTopBarLeftPropsGeneric = EditorTopBarLeftProps & InjectedIntlProps;

class EditorTopBarLeftIntl extends React.Component<EditorTopBarLeftPropsGeneric, EditorTopBarLeftState> {
  state = {
    isOpen: false,
    heightSettings: 650,
  };

  componentDidMount() {
    window.addEventListener('resize', this.resize);
    const element: any = document.querySelector('.editor-panels');
    if (element) {
      this.setState({ heightSettings: element.clientHeight });
    }
  }

  componentDidUpdate(_: any, prevState: EditorTopBarLeftState) {
    if (this.state.isOpen && !prevState.isOpen) {
      const element: any = document.querySelector('.editor-panels');
      if (element) {
        this.setState({ heightSettings: element.clientHeight });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  resize = () => {
    const element: any = document.querySelector('.editor-panels');
    if (element) {
      this.setState({ heightSettings: element.clientHeight });
    }
  };

  handleOpen = () => {
    this.setState({ isOpen: true });
  };

  handleClose = (e: any) => {
    // This classnames represent elements that should be white-listed.
    // This is to prevent the settings popup to close when clicking in this elements
    // This elements belong to portals that can be open in the settings popup
    // Because portals are outside of the settings popup tree elements, this function will be called
    const classNamesCanClick: any = ['.tooltip-wbs__content', '.select-portal__content'];
    const findClicklable = e && e.target && classNamesCanClick.find((parentClass: string) => e.target.closest(parentClass));
    if (!findClicklable) {
      this.setState({ isOpen: false });
    }
  };

  render() {
    const {
      currentDelay,
      currentRegion,
      currentUniverse,
      isMultiSimulation,
      isChild,
      handlerChangeToMultiSimulation,
      intl,
      modifiers,
      settingsOptions,
      onClickCode,
      code,
      onClickResults,
      results,
      isSuperAlpha,
    } = this.props;
    const shouldBeDisabled = isMultiSimulation && isChild && isMobile('mobile');

    const handleClickMultiSimulation = () => {
      analytics.trackCustomEvent('sim_click_convert_to_multi_simulation');
      handlerChangeToMultiSimulation?.();
    };

    return (
      <div className="editor-top-bar-left">
        <div className="editor-top-bar-left__filters">
          <Button disabled={shouldBeDisabled} onClick={onClickCode} active={code} className="editor-top-bar-left__filters--buttons">
            <FormattedMessageHelper id="editorTopBarRight.code" />
          </Button>
          <Button disabled={shouldBeDisabled} onClick={onClickResults} active={results} className="editor-top-bar-left__filters--buttons">
            <FormattedMessageHelper id="editorTopBarRight.results" />
          </Button>
        </div>

        <div className="editor-top-bar-left__expression-settings">
          {settingsOptions && (
            <div className="intro-step-2 editor-top-bar-left__settings-button">
              <Popup
                trigger={
                  <Button
                    content={intl.formatMessage({
                      id: 'editorTopBarLeft.settings',
                      defaultMessage: localeData.en['editorTopBarLeft.settings'],
                    })}
                    className="editor-top-bar-left__settings-btn"
                    disabled={shouldBeDisabled}
                  />
                }
                // @ts-expect-error injectIntl(connect(...)) composition collapses the component's own-props under @types/react@19
                content={<EditorSettingsForm handleClose={this.handleClose} />}
                on="click"
                position="bottom center"
                basic={true}
                className="editor-top-bar-left__settings-popup"
                open={this.state.isOpen}
                onClose={this.handleClose}
                onOpen={this.handleOpen}
                style={{ maxHeight: this.state.heightSettings }}
                popperModifiers={[{ name: 'preventOverflow', enabled: true }]}
              />
            </div>
          )}
          <div className="editor-top-bar-left__type-label">
            <span>{`${currentRegion}/d${currentDelay}/${currentUniverse}`}</span>
          </div>
        </div>

        {!isSuperAlpha && isMultiSimulation && !isChild && (
          <Button className={addModifiers('editor-top-bar-left__button', modifiers)} onClick={handleClickMultiSimulation}>
            <FormattedMessageHelper
              tagName="span"
              id="editorTopBarLeft.convertToMultiSimulation"
              defaultMessage={localeData.en['editorTopBarLeft.convertToMultiSimulation']}
            />
          </Button>
        )}
      </div>
    );
  }
}

export const EditorTopBarLeft = injectIntl(EditorTopBarLeftIntl);
